/**
 * "New Stripe Connect Account" trigger + modal.
 *
 * Renders a primary button. When clicked, opens a centered modal where the
 * user picks a vendor and types a name. On submit the backend
 * (createOnboardingLink, new-connection variant):
 *   1. Inserts the payment_processor_config row.
 *   2. Creates a PLATFORM-CONTROLLED Stripe account for the merchant
 *      (controller.is_controller=true).
 *   3. Returns a Stripe-hosted onboarding URL we redirect to.
 *
 * Stripe redirects back to /stripe-connect/list?onboarding=complete&
 * processorId=N, where the list page refreshes status and prompts for a
 * Terminal Location.
 *
 * WHY PLATFORM-CREATED, NOT OAUTH (Aug 2026): OAuth-linked accounts —
 * including "sign up via OAuth" ones — are owned by the merchant, not
 * controlled by Ronin, and Stripe refuses to deploy the Ronin POS app to
 * card readers registered on accounts the platform doesn't control. Every
 * OAuth merchant's S700s eventually boot to the Stripe splash screen instead
 * of our app. Platform-created accounts with the full-dashboard controller
 * configuration keep the merchant experience that made us adopt OAuth in the
 * first place (their own stripe.com login, full dashboard, they pay their
 * own fees and own their losses — Ronin takes on no risk) while making the
 * account deployable. Account control is immutable, so this choice can only
 * be made here, at creation.
 */

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { LinkIcon } from "@heroicons/react/24/outline";
import { PrimaryButton } from "../../components/Button";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { organizationStore } from "../../store/organization";
// Role comes from the access token, not sessionStorage — see
// providers/AuthProvider.getDashboardRole for why that distinction matters.
import { isAdminUser } from "../../providers/AuthProvider";
import { userStore } from "../../store/user";
import { VENDORS_LIST_QUERY } from "../vendors/queries";
import { createOnboardingLinkForNew } from "./api";

// Minimal shape we care about from the vendors query for the picker.
interface VendorOption {
  id: number;
  name: string;
}

const client = createApolloClient();


interface Props {
  /**
   * Where to send the user after they finish (or abandon) Stripe-hosted
   * onboarding. `{processorId}` is interpolated into both URLs.
   * Default: /payment-processors/list.
   */
  returnPath?: string;
  /** Optional callback after creation succeeds (before the Stripe redirect). */
  onCreated?: (newProcessorId: number) => void;
  /** Optional label override for the trigger button. */
  label?: string;
}

export const NewStripeConnectAccountModal = ({
  returnPath = "/payment-processors/list",
  label = "New Stripe Connect Account",
}: Props) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const admin = isAdminUser();

  // Vendor users don't pick a vendor — we already know who they are. The
  // picker only renders for users without a vendor_id (admins / organizers
  // creating accounts on a vendor's behalf).
  const currentUser = userStore.getState().user;
  const lockedVendor: VendorOption | null = currentUser?.vendor_id
    ? {
        id: Number(currentUser.vendor_id),
        name: currentUser.vendor?.name ?? "",
      }
    : null;

  // Most vendor-scoped users carry their vendor(s) in the "Restrict to
  // Vendor(s)" list (user.vendors — vendor ids, same field the rest of the
  // app filters on with vendor_id:{_in:...}) rather than vendor_id. The
  // vendor query below is filtered to this list; when it resolves to exactly
  // one vendor, the picker is hidden and that vendor is auto-selected.
  const restrictedVendorIds: number[] | null =
    currentUser?.vendors && currentUser.vendors.length > 0
      ? currentUser.vendors.map((v) => Number(v)).filter((n) => !Number.isNaN(n))
      : null;

  // The processor row belongs to an ORGANIZATION (the organizer). For
  // non-admin users that's their own org from their login — authoritative,
  // regardless of what's picked in the header. Admins act across orgs, so
  // for them the header selection decides. (The backend enforces the same
  // rule server-side: non-admins can only create under their own org.)
  const resolveOrganizationId = (): number | null => {
    const rawHeaderOrg = organizationStore.getState().organizationId;
    const headerOrgId =
      rawHeaderOrg != null && parseInt(rawHeaderOrg.toString(), 10)
        ? parseInt(rawHeaderOrg.toString(), 10)
        : null;
    if (admin) return headerOrgId;
    return currentUser?.organization_id
      ? Number(currentUser.organization_id)
      : headerOrgId;
  };
  // Default to test mode if the caller is admin — usually what admins want
  // for development. Non-admins never see the option (always live).
  const [useTestMode, setUseTestMode] = useState<boolean>(admin);

  // Vendor picker. Required — every Stripe Connect account gets anchored to a
  // vendor record so ops can still identify "whose Stripe account is this"
  // even when the merchant chose a Stripe display name that doesn't match.
  const [vendorId, setVendorId] = useState<number | "">("");
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);

  // Load the org's vendors when the modal opens. Skipped entirely for
  // vendor users (their vendor is locked) and when no org is selected
  // (handleCreate will block submit in that case anyway).
  useEffect(() => {
    if (!open || lockedVendor) return;
    const orgId = resolveOrganizationId();
    if (!orgId) return;
    setLoadingVendors(true);
    client
      .query({
        query: VENDORS_LIST_QUERY,
        variables: {
          where: {
            organization_id: { _eq: orgId },
            is_active: { _eq: true },
            // Vendor-restricted users only ever see (and create for) their
            // own vendors — same _in filter the rest of the app uses.
            ...(restrictedVendorIds
              ? { id: { _in: restrictedVendorIds } }
              : {}),
          },
        },
        fetchPolicy: "network-only",
      })
      .then((res) => {
        const rows = (res.data?.vendors ?? []) as VendorOption[];
        setVendors(rows);
        // Exactly one allowed vendor → select it; the picker is hidden below.
        if (restrictedVendorIds && rows.length === 1) {
          setVendorId(rows[0].id);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load vendors for Stripe Connect modal", err);
        toast.error("Could not load vendor list");
      })
      .finally(() => setLoadingVendors(false));
  }, [open]);

  const close = () => {
    if (busy) return;
    setOpen(false);
    setName("");
    setVendorId("");
  };

  // The single vendor this user can act as, however it's expressed: an
  // explicit vendor_id on the user, or a one-vendor restriction list. Null
  // means they legitimately choose from the (possibly restricted) picker.
  const soleVendor: VendorOption | null =
    lockedVendor ??
    (restrictedVendorIds && vendors.length === 1 ? vendors[0] : null);

  const handleCreate = async () => {
    const effectiveVendorId = soleVendor?.id ?? vendorId;
    if (!effectiveVendorId) {
      toast.error("Please pick a vendor");
      return;
    }
    if (!name.trim()) {
      toast.error("Please enter a name for this Stripe account");
      return;
    }
    const currentUserId = userStore.getState().user?.id;
    const organizationId = resolveOrganizationId();
    if (!currentUserId) {
      toast.error("Not logged in");
      return;
    }
    if (!organizationId) {
      toast.error(
        admin
          ? "Pick an organization in the header first"
          : "Your login isn't tied to an organization — contact Ronin support"
      );
      return;
    }

    // Locked/sole vendor needs no list lookup — the backend re-validates
    // vendor↔org either way.
    const chosenVendor =
      soleVendor ?? vendors.find((v) => v.id === effectiveVendorId) ?? null;
    if (!chosenVendor) {
      toast.error("Selected vendor is not in the list — try reopening the modal");
      return;
    }

    // payment_processor label format: "<Vendor Name> — <User-typed name>" —
    // composed on the backend from the authoritative vendor record.

    setBusy(true);
    try {
      const stripeMode = admin ? (useTestMode ? "test" : "live") : undefined;

      // The backend creates the processor row AND the platform-controlled
      // Stripe account, then hands back a Stripe-hosted onboarding URL. The
      // "{processorId}" placeholder is interpolated server-side once the row
      // exists — the list page's ?onboarding=complete handler picks it up on
      // return and prompts for a Terminal Location.
      const returnUrl = `${window.location.origin}/stripe-connect/list?onboarding=complete&processorId={processorId}`;
      const refreshUrl = `${window.location.origin}/stripe-connect/list?onboarding=refresh&processorId={processorId}`;
      const { url } = await createOnboardingLinkForNew({
        organizationId,
        vendorId: chosenVendor.id,
        descriptor: name.trim(),
        userId: typeof currentUserId === "number" ? currentUserId : undefined,
        returnUrl,
        refreshUrl,
        mode: stripeMode,
      });
      window.location.href = url;
    } catch (err: any) {
      toast.error(err.message || "Failed to start Stripe onboarding");
      setBusy(false);
    }
  };

  return (
    <>
      <PrimaryButton onClick={() => setOpen(true)}>
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <LinkIcon className="w-4 h-4" />
          {label}
        </span>
      </PrimaryButton>

      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={close}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 pointer-events-none">
            <div
              className="pointer-events-auto w-full max-w-xl bg-white border border-gray-200 rounded-lg shadow-lg p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-base font-semibold mb-1">
                New Stripe Connect account
              </div>
              <div className="text-xs text-gray-500 mb-4">
                {soleVendor ? (
                  <>
                    Give this Stripe account a name so you can tell your
                    accounts apart — e.g. "Main", "East Stand", "Food truck".
                  </>
                ) : (
                  <>
                    Pick the vendor this Stripe account belongs to, then give
                    the account its own descriptor. We save the processor as
                    <em> "&lt;Vendor&gt; — &lt;your name&gt;"</em> so ops can
                    always trace which vendor a Stripe account is for, even
                    when the merchant's Stripe name doesn't match.
                  </>
                )}
              </div>

              {soleVendor ? (
                soleVendor.name && (
                  <div className="mb-3 text-xs text-gray-500">
                    Creating a Stripe account for{" "}
                    <span className="font-medium text-gray-700">
                      {soleVendor.name}
                    </span>
                  </div>
                )
              ) : (
                <>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Vendor
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 mb-3 bg-white disabled:bg-gray-100"
                    value={vendorId}
                    onChange={(e) =>
                      setVendorId(e.target.value ? parseInt(e.target.value, 10) : "")
                    }
                    disabled={busy || loadingVendors || vendors.length === 0}
                  >
                    <option value="">
                      {loadingVendors
                        ? "Loading vendors…"
                        : vendors.length === 0
                          ? "No vendors on this organization — create one first"
                          : "Select a vendor…"}
                    </option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </>
              )}

              <label className="block text-xs font-medium text-gray-700 mb-1">
                This account's name
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 mb-3"
                placeholder="e.g. Main Stripe account, East Stand, POS #2"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                disabled={busy}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                  if (e.key === "Escape") close();
                }}
              />
              {admin && (
                <label className="flex items-center gap-2 mb-4 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    checked={useTestMode}
                    onChange={(e) => setUseTestMode(e.target.checked)}
                    disabled={busy}
                  />
                  <span>
                    Create as <strong>test mode</strong> account (admin-only).
                    Uncheck to create a real live Stripe account.
                  </span>
                </label>
              )}
              <div className="mb-3 text-xs text-gray-500 border border-gray-200 bg-gray-50 rounded p-2 leading-snug">
                <strong>Heads up:</strong> Stripe will walk you through
                creating a new account — business info, bank account, and
                identity verification. Takes ~5 minutes. The account is yours
                — you'll see it on stripe.com under your own login when you're
                done, you pay your own Stripe fees, and your money goes
                straight to your bank.
              </div>
              <div className="mb-3 text-xs text-amber-700 border border-amber-200 bg-amber-50 rounded p-2 leading-snug">
                <strong>Already have a Stripe account?</strong> You'll still
                create a new one here — Stripe only lets Ronin's card readers
                run our point-of-sale app on accounts set up through Ronin.
                Your existing account isn't affected. Tip: sign up with the
                same email you use for Stripe today, and both accounts will
                show up under that one login.
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={close}
                  disabled={busy}
                  className="whitespace-nowrap rounded border border-gray-300 bg-white text-gray-700 text-sm font-medium px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={!busy ? handleCreate : undefined}
                  disabled={busy}
                  className="whitespace-nowrap rounded bg-primary-600 text-white text-sm font-medium px-4 py-2 hover:bg-primary-700 disabled:opacity-50"
                >
                  {busy ? "Working..." : "Sign up & Connect"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
